import { useVaultStrings } from "module/vault/hook/useVaultStrings"
import { Web3WalletContainer } from "module/wallet/container/Web3WalletContainer"
import { formatUSD } from "util/format"

import { Box, Divider, HStack, Icon, SimpleGrid, Text } from "@chakra-ui/react"
import { Card } from "component/Card"
import { AccountValue, FreeCollateral } from "component/Icon"
import { Jazzicon } from "component/JazzIdentity/JazzIdentity"
import { LiveStats } from "component/LiveStats"
import { MagicGroup } from "component/MagicGroup"
import { MotionBox } from "component/Motion"
import { Stats } from "component/Stats"
import { Vault } from "sdk-react/vault/useVault"
import useResizeObserver from "use-resize-observer"

export function AccountCard() {
    const { account } = Web3WalletContainer.useContainer()

    const { accountValueStr, buyingPowerStr, netValueUsd } = useVaultStrings()
    const { freeCollateral } = Vault.useContainer()

    // use use-resize-observer and add ref to card
    const { ref, width = 1 } = useResizeObserver<HTMLDivElement>()

    return (
        <Card ref={ref} h="full" minH="auto" position="relative" overflow="visible" zIndex={1}>
            <SimpleGrid
                py={width < 590 ? 2 : 0}
                columns={width < 590 ? 1 : 2}
                gap={width < 590 ? 3 : 0}
                position="relative"
                zIndex={1}
                width="100%"
                alignItems={width < 590 ? "center" : "flex-end"}
                flexGrow={1}
                h="full"
            >
                <HStack py={2} spacing={4}>
                    <Box bg="blackAlpha.700" borderRadius="xl" p={2}>
                        <Icon as={AccountValue} boxSize="32px" color="#3CEAAA" />
                    </Box>
                    <LiveStats
                        size="3xl"
                        labelColor="rgba(255,255,255,0.75)"
                        label="Net USD Value"
                        dollarSign
                        number={netValueUsd?.toNumber() || 0}
                    />
                </HStack>

                <MagicGroup>
                    <HStack py={2} spacing={4}>
                        <Box bg="blackAlpha.700" borderRadius="xl" p={2}>
                            <Icon as={FreeCollateral} boxSize="32px" color="#3CEAAA" />
                        </Box>
                        <LiveStats
                            showTooltip={true}
                            size="3xl"
                            labelColor="rgba(255,255,255,0.75)"
                            label="Free Collateral"
                            dollarSign
                            number={freeCollateral?.toNumber() || 0}
                        />
                    </HStack>

                    <Text fontSize={"sm"} color="gray.400">
                        The weighted value of your free collaterals.
                    </Text>
                    <Divider />
                    <Stats
                        size="sm"
                        variant="left"
                        labelColor="rgba(255,255,255,0.75)"
                        label="Buying Power"
                        number={"$" + buyingPowerStr}
                    />
                    <Stats
                        size="sm"
                        variant="left"
                        labelColor="rgba(255,255,255,0.75)"
                        label="Total Collateral"
                        number={formatUSD(accountValueStr)}
                    />
                </MagicGroup>
            </SimpleGrid>
            {/* eth avatar color bg */}
            <Box
                zIndex={-1}
                overflow="hidden"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg="rgba(0,0,0,0.1)"
                borderRadius={"2xl"}
                position={"absolute"}
                sx={{ backdropFilter: "blur(40px)" }}
            >
                <MotionBox
                    sx={{ filter: "blur(40px) brightness()" }}
                    initial={{ scale: 1.6, opacity: 1, rotate: 25, y: "0%" }}
                    position="absolute"
                    zIndex={-2}
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    display="flex"
                    justifyContent="center"
                >
                    {account && <Jazzicon diameter={400} address={account} />}
                </MotionBox>
            </Box>
        </Card>
    )
}
